import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MachineAccounts } from '@shared/entities/machineAccounts';
import { Paginate } from '@shared/entities/paginate';
import { ApiService, Pagination, PER_PAGE } from '@shared/services';
import { BehaviorSubject, Observable, Subject, catchError, map, of, shareReplay, switchMap, take, tap } from 'rxjs';

const LOG_PREFIX = '[Api][MachineAccounts] ';

export interface MachineAccountsFilterURL {
  accountName?: string;
  size?: number;
  page?: number;
}

@Injectable({
  providedIn: 'root'
})
export class MachineAccountsService extends ApiService<MachineAccounts> {
  private _refresh$: Subject<Pagination>;
  private _allMachineAccounts$: Observable<Paginate<MachineAccounts>>;
  private _currentPaginator: Pagination;
  private _currentFilters: MachineAccountsFilterURL = {};

  set filters(value: MachineAccountsFilterURL) {
    this._currentFilters = value;
    this.refresh(value.page || 1, value.size || PER_PAGE);
  }

  get filters(): MachineAccountsFilterURL {
    return this._currentFilters;
  }

  constructor(private _activatedRoute: ActivatedRoute) {
    const masterUrl = '/machines/accounts/all';
    const detailUrl = '/machines/accounts/all';

    super(MachineAccounts, masterUrl, detailUrl);

    this._currentPaginator = { page: 1, perPage: PER_PAGE };

    this._refresh$ = new BehaviorSubject<Pagination>(this._currentPaginator);
    this._allMachineAccounts$ = this._refresh$.pipe(
      switchMap((pagination) => {
        // Handle locationId from query params if needed
        const { locationId } = this._activatedRoute.snapshot.queryParams;
        if (locationId) {
          this.setUrlParameter('locationId', locationId);
        }

        // Set pagination parameters
        this.setUrlParameter('page', pagination.page.toString());
        this.setUrlParameter('size', pagination.perPage.toString());

        // Set accountName filter if provided
        if (this._currentFilters.accountName !== undefined) {
          this.setUrlParameter('accountName', this._currentFilters.accountName);
        }

        return this.paginate(MachineAccounts, pagination.page, pagination.perPage);
      }),
      tap({
        next: (result) => this.logger.debug(LOG_PREFIX + 'all$ machine accounts', result),
        error: (err) => this.logger.error(LOG_PREFIX + 'all$ machine accounts error', err)
      }),
      shareReplay(1)
    );
  }

  /**
   * Get all machine accounts with pagination
   */
  get allMachineAccounts$(): Observable<Paginate<MachineAccounts>> {
    return this._allMachineAccounts$;
  }

  /**
   * Get all machine accounts as a simple array (without pagination wrapper)
   */
  getAllMachineAccounts(): Observable<MachineAccounts[]> {
    this.logger.debug(LOG_PREFIX + 'getAllMachineAccounts');
    return this.paginate(MachineAccounts, 1, 100).pipe(
      map((paginate) => paginate.data || []),
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'getAllMachineAccounts error', error);
        return of([]);
      })
    );
  }

  /**
   * Refresh machine accounts with pagination
   */
  refresh(page: number, perPage: number = PER_PAGE) {
    this.logger.debug(LOG_PREFIX + 'refresh machine accounts');
    this._currentPaginator = { page, perPage };
    this._refresh$.next(this._currentPaginator);
    return this._allMachineAccounts$.pipe(take(1));
  }

  /**
   * Reset filters and refresh
   */
  refreshFilters() {
    this.logger.debug(LOG_PREFIX + 'refresh filters');
    this._currentFilters = {};
    this.refresh(1, PER_PAGE);
  }

  /**
   * Search machine accounts by account name
   */
  searchByAccountName(
    accountName: string,
    page = 1,
    size: number = PER_PAGE
  ): Observable<Paginate<MachineAccounts>> {
    this.logger.debug(LOG_PREFIX + 'searchByAccountName', accountName);

    this.setUrlParameter('accountName', accountName);
    this.setUrlParameter('page', page.toString());
    this.setUrlParameter('size', size.toString());

    return this.paginate(MachineAccounts, page, size).pipe(
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'searchByAccountName error', error);
        return of({ data: [], total: 0, page: page, size: size } as Paginate<MachineAccounts>);
      })
    );
  }

  /**
   * Get machine accounts with custom parameters
   */
  getMachineAccounts(
    params: {
      page?: number;
      size?: number;
      accountName?: string;
    } = {}
  ): Observable<Paginate<MachineAccounts>> {
    this.logger.debug(LOG_PREFIX + 'getMachineAccounts', params);

    // Clear previous parameters
    Array.from(this.getUrlParameters().keys()).forEach((key) => this.removeUrlParameter(key));

    // Set new parameters
    if (params.page) this.setUrlParameter('page', params.page.toString());
    if (params.size) this.setUrlParameter('size', params.size.toString());
    if (params.accountName) this.setUrlParameter('accountName', params.accountName);

    return this.paginate(MachineAccounts, params.page || 1, params.size || PER_PAGE).pipe(
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'getMachineAccounts error', error);
        return of({
          data: [],
          total: 0,
          page: params.page || 1,
          size: params.size || PER_PAGE
        } as Paginate<MachineAccounts>);
      })
    );
  }
}
